import Ajv2020 from 'ajv/dist/2020';
import appDefinitionV2Schema from '../../../packages/builder-schema/schemas/app-definition.v2.schema.json';
import { getPanelContractDiagnostics, normalizePanelBindings, resolveStructuredPath, validatePanelBindingContract } from '../components/studio/studioV2AppBindingMapper';

/** Component panels that talk to something other than a flow. */
const FLOWLESS_COMPONENTS = new Set(['skill_chat']);

let _validate = null;
function getValidateApp() {
  if (!_validate) {
    const ajv = new Ajv2020({ allErrors: true, strict: false });
    _validate = ajv.compile(appDefinitionV2Schema);
  }
  return _validate;
}

export function validateStudioV2AppDefinition(definition) {
  const validate = getValidateApp();
  const ok = validate(definition);
  const errors = (validate.errors || []).map((er) => ({
    instancePath: er.instancePath || '',
    message: er.message || 'invalid',
  }));
  return { valid: !!ok, errors };
}

export function getPreviewPanelOutputs(definition) {
  const interviewFlow = definition?.metadata?.interviewFlow;
  const previewPanelOutputs = interviewFlow?.previewPanelOutputs;
  return previewPanelOutputs && typeof previewPanelOutputs === 'object' && !Array.isArray(previewPanelOutputs)
    ? previewPanelOutputs
    : {};
}

export function buildAppPublishReadiness(definition, options = {}) {
  const analysis = analyzeStudioV2AppDefinition(definition, options);
  const panels = Array.isArray(definition?.panels) ? definition.panels : [];
  const componentPanels = panels.filter((p) => p?.type === 'component' && !FLOWLESS_COMPONENTS.has(p.componentType));
  const previewPanelOutputs = getPreviewPanelOutputs(definition);
  const interviewFlow = definition?.metadata?.interviewFlow || {};
  const publicRouteSafe = definition?.visibility !== 'public' || Boolean(interviewFlow.publicRouteReviewed);
  const sharedChatReviewed = !interviewFlow.sharedChatHistoryAcrossPanels || Boolean(interviewFlow.sharedChatSettingsReviewed);
  const items = [
    { id: 'schema', label: 'Definition schema is valid', ok: analysis.schemaOk },
    { id: 'flows', label: 'Component panels have flow refs or preview fixtures', ok: componentPanels.every((p) => p.source?.flowRef || previewPanelOutputs[p.id]) },
    { id: 'bindings', label: 'Panel bindings have no blocking diagnostics', ok: analysis.errors.length === 0 },
    { id: 'preview', label: 'Preview data is available for at least one panel', ok: Object.keys(previewPanelOutputs).length > 0 },
    { id: 'public-route', label: 'Public route settings reviewed', ok: publicRouteSafe },
    { id: 'shared-chat', label: 'Shared chat settings reviewed', ok: sharedChatReviewed },
  ];
  return {
    ready: items.every((item) => item.ok),
    items,
    errors: analysis.errors,
    warnings: analysis.warnings,
  };
}

export function analyzeStudioV2AppDefinition(definition, options = {}) {
  const errors = [];
  const warnings = [];
  const hintsByFlowRef = options.hintsByFlowRef || {};
  const defaultOutputHints = Array.isArray(options.outputHints) ? options.outputHints : [];
  const schema = validateStudioV2AppDefinition(definition || {});
  if (!schema.valid) {
    for (const er of schema.errors || []) {
      errors.push(`${er.instancePath || '/'}: ${er.message || 'invalid'}`);
    }
  }
  const panels = Array.isArray(definition?.panels) ? definition.panels : [];
  const seen = new Set();
  for (const p of panels) {
    if (!p?.id) {
      errors.push('Panel missing id');
      continue;
    }
    if (seen.has(p.id)) errors.push(`Duplicate panel id: ${p.id}`);
    seen.add(p.id);
    if (p.type === 'component' && p.componentType === 'skill_chat') {
      if (!p.config?.skillId) errors.push(`Panel "${p.id}": choose a skill for the skill chat`);
    } else if (p.type === 'component') {
      if (!p.componentType) warnings.push(`Panel "${p.id}": missing componentType`);
      if (p.componentType === 'plugin' && !p.config?.pluginPanel) errors.push(`Panel "${p.id}": choose a plugin panel`);
      const ref = p.source?.flowRef || p.flowId;
      if (!ref) errors.push(`Panel "${p.id}": source.flowRef is required for component panels`);
      const refKey = String(p.source?.flowRef || p.flowId || '').trim();
      const outputHints = hintsByFlowRef[refKey] || defaultOutputHints;
      const contract = validatePanelBindingContract(p, outputHints);
      if (!contract.valid) {
        contract.errors.forEach((er) => errors.push(`Panel "${p.id}": ${er}`));
      }
      const panelContract = getPanelContractDiagnostics(p, outputHints);
      panelContract.messages.forEach((msg) => warnings.push(`Panel "${p.id}": ${msg}`));
      if (p.componentType === 'form') {
        const ff = p.config?.formFields;
        if (!Array.isArray(ff) || ff.length === 0) {
          warnings.push(`Panel "${p.id}" (form): no formFields in config — add fields in Studio or JSON`);
        } else {
          const keys = new Set();
          ff.forEach((f, i) => {
            const k = String(f?.id || f?.name || '').trim();
            if (!k) warnings.push(`Panel "${p.id}" (form): field #${i + 1} missing id/name`);
            else if (keys.has(k)) warnings.push(`Panel "${p.id}" (form): duplicate field id "${k}"`);
            else keys.add(k);
          });
        }
      }
    }
  }
  return { schemaOk: schema.valid, errors, warnings, panelCount: panels.length };
}

/** Compare panel bindings against keys present in a last-run / streamed payload. */
export function compareBindingsWithLastRun(definition, lastRunRoot) {
  if (!lastRunRoot || typeof lastRunRoot !== 'object') {
    return { ok: false, missing: [], message: 'No last run payload to compare' };
  }
  const missing = [];
  (definition?.panels || []).forEach((p) => {
    if (p?.type !== 'component') return;
    normalizePanelBindings(p?.source).forEach((b) => {
      if (resolveStructuredPath(lastRunRoot, b.sourcePath) === undefined) {
        missing.push({ panelId: p.id, targetKey: b.targetKey, sourcePath: b.sourcePath });
      }
    });
  });
  return {
    ok: missing.length === 0,
    missing,
    message: missing.length
      ? `${missing.length} binding path(s) missing from last run`
      : 'All bindings resolved against last run',
  };
}
